import React, { useState, useEffect, Suspense } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getAccessToken, fetchSimilarContactsSigned, fetchSimilarContactsSignedAdvanced } from '../services/dynamicsApi';
import { sortAccountsByScore, sortAccountsByHealthScore } from '../services/scoring';
import { ContactSelectorSkeleton, AccountResultsSkeleton } from '../components/Skeletons';
import LanguageSelector from '../components/LanguageSelector';
import SearchForm from '../components/SearchForm';
import LoginPage from './LoginPage';

// Lazy loading des composants
const ContactSelector = React.lazy(() => import('../components/ContactSelector'));
const AccountResults = React.lazy(() => import('../components/AccountResults'));

const MainApp = () => {
  const isAuthenticated = useIsAuthenticated();
  const { instance } = useMsal();
  const [activeTab, setActiveTab] = useState('contact'); // 'contact' or 'advanced'
  const [searchCriteria, setSearchCriteria] = useState({});
  const [similarAccounts, setSimilarAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const [accessToken, setAccessToken] = useState(null);
  const { t } = useTranslation();

  useEffect(() => {
    document.body.className = darkMode ? 'dark' : 'light';
  }, [darkMode]);

  // Acquire token eagerly so child components can use it
  useEffect(() => {
    const acquireToken = async () => {
      try {
        const token = await getAccessToken(instance);
        setAccessToken(token);
      } catch (error) {
        console.error('Error acquiring initial token:', error);
      }
    };
    if (isAuthenticated) {
      acquireToken();
    }
  }, [isAuthenticated, instance]);

  const handleContactSelect = async (contact) => {
    setSelectedContact(contact);
    const parentAccount = contact.parentcustomerid_account;
    if (!parentAccount) {
      console.error(t('contactWithoutAccount'));
      return;
    }

    const fullCriteria = {
      industrycode: parentAccount.industrycode,
      '_dimsi_countryid_value': parentAccount['_dimsi_countryid_value'],
      '_dimsi_zoneid_value': parentAccount['_dimsi_zoneid_value'],
      dimsi_nbofemployees: contact.dimsi_nbofemployees,
    };

    setSearchCriteria(fullCriteria);
    setIsLoading(true);

    try {
      const token = await getAccessToken(instance);

      // Essayer avec tous les critères
      let accounts = await fetchSimilarContactsSigned(token, fullCriteria);
      let usedCriteria = fullCriteria;

      // Si aucun résultat, essayer en retirant les critères moins importants
      if (accounts.length === 0) {
        const criteriaOrder = ['dimsi_nbofemployees', '_dimsi_zoneid_value']; // Moins importants d'abord
        for (const criterion of criteriaOrder) {
          const reducedCriteria = { ...usedCriteria };
          delete reducedCriteria[criterion];
          accounts = await fetchSimilarContactsSigned(token, reducedCriteria);
          if (accounts.length > 0) {
            usedCriteria = reducedCriteria;
            setSearchCriteria(usedCriteria);
            break;
          }
        }
      }

      const sortedAccounts = sortAccountsByScore(accounts, usedCriteria);
      setSimilarAccounts(sortedAccounts);
    } catch (error) {
      console.error('Erreur lors de la recherche:', error);
      toast.error(t('searchError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAdvancedSearch = async (odataFilter, formData) => {
    setSearchCriteria(formData);
    setSelectedContact(null); // Reset selected contact when doing advanced search
    setIsLoading(true);

    try {
      const token = await getAccessToken(instance);
      const accounts = await fetchSimilarContactsSignedAdvanced(token, odataFilter);

      // For advanced search, sort by Health Score priority (best health first)
      // Score/criteria columns are hidden in this mode since the OData filter already matches
      const sortedAccounts = sortAccountsByHealthScore(accounts);
      setSimilarAccounts(sortedAccounts);

      if (accounts.length === 0) {
        toast.info(t('noResults'));
      }
    } catch (error) {
      console.error('Erreur lors de la recherche avancée:', error);
      toast.error(t('searchError'));
    } finally {
      setIsLoading(false);
    }
  };

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  const activeAccount = instance.getActiveAccount();
  const userName = activeAccount ? activeAccount.name || activeAccount.username : '';
  const userInitials = userName ? userName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : '';

  return (
    <>
      <header className="app-header">
        <div className="header-left">
          <div className="header-logo-container">
            <div className="logo-circle"></div>
          </div>
          <h1>{t('title', { defaultValue: 'LeadsMatch' })}</h1>
        </div>
        <div className="header-right">
          <LanguageSelector />
          <button
            onClick={() => setDarkMode(!darkMode)}
            className="theme-toggle"
            title={darkMode ? t('switchToLight') : t('switchToDark')}
          >
            {darkMode ? '☀️' : '🌙'}
          </button>

          {userName && (
            <div className="user-profile" title={userName}>
              <div className="avatar">{userInitials}</div>
              <span className="user-name">{userName.split(' ')[0]}</span>
            </div>
          )}
        </div>
      </header>

      <div className="App">

      {/* Tab Navigation */}
      <div className="search-tabs">
        <button
          className={`tab-button ${activeTab === 'contact' ? 'active' : ''}`}
          onClick={() => setActiveTab('contact')}
        >
          {t('contactBasedSearch') || 'Recherche par Contact'}
        </button>
        <button
          className={`tab-button ${activeTab === 'advanced' ? 'active' : ''}`}
          onClick={() => setActiveTab('advanced')}
        >
          {t('advancedSearch') || 'Recherche Avancée'}
        </button>
      </div>

      {/* Contact-based Search Tab */}
      {activeTab === 'contact' && (
        <div className="contact-selector">
          <Suspense fallback={<ContactSelectorSkeleton />}>
            <ContactSelector onContactSelect={handleContactSelect} />
          </Suspense>
        </div>
      )}

      {/* Advanced Search Tab */}
      {activeTab === 'advanced' && (
        <div className="advanced-search-container">
          <SearchForm
            onSearch={handleAdvancedSearch}
            accessToken={accessToken}
            isLoading={isLoading}
          />
        </div>
      )}

      <div className="account-results">
        <Suspense fallback={<AccountResultsSkeleton />}>
          <AccountResults
            accounts={similarAccounts}
            searchCriteria={searchCriteria}
            selectedContact={selectedContact}
            isLoading={isLoading} />
        </Suspense>

        {isLoading && (
          <div className="search-loading-indicator">
            {t('searching')}
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default MainApp;
